const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;
const DESIGN_BASE_FONT_SIZE = 16;
/**
 * Calculate base font size for a container based on its measured dimensions
 * relative to a Figma design reference.
 *
 * How it works:
 *   - The Figma design is authored at a specific reference size (e.g. 1920×1080)
 *     with a 16px base font size.
 *   - When the real container is smaller/larger, we scale the base font size by
 *     the same ratio so that every `em`-based value inside stays proportional.
 *   - We use the *limiting dimension* (the one that hits its limit first) to
 *     avoid clipping content when the container's aspect ratio differs from
 *     the design's aspect ratio.
 *
 * Usage in components that need their own scaling context (e.g. overlays):
 *   const fontSize = getBaseFontSizeFromContainerDimensions({
 *     width, height,
 *     designWidth: 1920, designHeight: 1080,
 *   });
 *   // Apply as inline style: style={{ fontSize: `${fontSize}px` }}
 *   // Children using em units will then scale correctly.
 */
export const getBaseFontSizeFromContainerDimensions = ({
  width,
  height,
}: {
  width: number;
  height: number;
}): number => {
  const designAspectRatio = DESIGN_WIDTH / DESIGN_HEIGHT;
  const actualAspectRatio = width / height;

  // Scale by the dimension that fills first so content is never clipped
  const scalingFactor =
    actualAspectRatio > designAspectRatio
      ? height / DESIGN_HEIGHT
      : width / DESIGN_WIDTH;

  return DESIGN_BASE_FONT_SIZE * scalingFactor;
};
